import { DealType } from 'model/deals.model';

export type DetailField = {
  label: string;
  value: string | number | boolean | null | undefined;
  fieldName: string;
  isAvatar?: boolean;
  isSelect?: boolean;
  isChip?: boolean;
  hasActivity?: boolean;
  format?: (value: any) => string;
};

export const capitalizeStage = (stage: string): string => {
  if (!stage) return stage;
  return stage
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

export const formatPipelineType = (value: string): string => {
  if (!value || value === 'N/A') return value;
  return value
    .replace(/_/g, '/')
    .split('/')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join('/');
};

const getValue = (flatDeal: any, deal: any, fieldName: string | string[]): any => {
  if (Array.isArray(fieldName)) {
    for (const name of fieldName) {
      const value = flatDeal[name] || deal[name];
      if (value !== undefined && value !== null && value !== '') {
        return value;
      }
    }
    return 'N/A';
  }
  return flatDeal[fieldName] || deal[fieldName] || 'N/A';
};

const formatCurrency = (value: any): string => {
  if (!value || value === 'N/A') return 'N/A';
  return `$${Number(value).toLocaleString()}`;
};

const formatPercentage = (value: any): string => {
  if (!value || value === 'N/A') return 'N/A';
  return `${value}%`;
};

const formatDate = (value: any): string => {
  if (!value || value === 'N/A') return 'N/A';
  return new Date(value).toLocaleDateString();
};

const formatBoolean = (value: boolean | undefined, fallback?: boolean): string => {
  if (value !== undefined) return value ? 'Yes' : 'No';
  if (fallback !== undefined) return fallback ? 'Yes' : 'No';
  return 'N/A';
};

export const mapDealToDetailFields = (
  deal: any,
  dealType: DealType,
): {
  opportunityInfo: DetailField[];
  financialInfo: DetailField[];
  attributionTracking: DetailField[];
  additionalInfo: DetailField[];
  typeSpecificFields: DetailField[];
} => {
  const dealDetails = deal.dealDetails || {};
  const flatDeal = { ...deal, ...dealDetails };

  const commonOpportunityFieldsFirst: DetailField[] = [
    {
      label: 'Opportunity name',
      value: getValue(flatDeal, deal, ['name', 'title']),
      fieldName: 'name',
    },
    {
      label: 'Client name',
      value: getValue(flatDeal, deal, ['clientName', 'client']),
      fieldName: 'clientName',
      isAvatar: true,
    },
    {
      label: 'Opportunity owner',
      value: getValue(flatDeal, deal, ['ownerName', 'owner']),
      fieldName: 'ownerName',
      isAvatar: true,
      isSelect: true,
    },
    {
      label: 'Pipeline type',
      value: getValue(flatDeal, deal, ['pipelineType', 'dealType']),
      fieldName: 'pipelineType',
      isChip: true,
    },
    {
      label: 'Stage',
      value: getValue(flatDeal, deal, ['stageName', 'stage']),
      fieldName: 'stageName',
      isSelect: true,
    },
    {
      label: 'Probability (%)',
      value: getValue(flatDeal, deal, 'probability'),
      fieldName: 'probability',
      format: formatPercentage,
    },
  ];

  const commonOpportunityFieldsSecond: DetailField[] = [
    {
      label: 'Revenue tier',
      value: getValue(flatDeal, deal, 'revenueTier'),
      fieldName: 'revenueTier',
    },
    {
      label: 'Ceiling tier',
      value: getValue(flatDeal, deal, 'ceilingTier'),
      fieldName: 'ceilingTier',
    },
    {
      label: 'Client orientation',
      value: getValue(flatDeal, deal, 'clientOrientation'),
      fieldName: 'clientOrientation',
    },
    {
      label: 'Career stage',
      value: getValue(flatDeal, deal, ['careerState', 'careerStage']),
      fieldName: 'careerState',
    },
  ];

  const commonFinancialFields: DetailField[] = [
    {
      label: 'Deal value (gross)',
      value: getValue(flatDeal, deal, ['dealValue', 'amount', 'value']),
      fieldName: 'dealValue',
      format: (v) => (v === 'N/A' ? 'N/A' : formatCurrency(v)),
    },
    {
      label: 'Commission rate (%)',
      value: getValue(flatDeal, deal, 'commissionRate'),
      fieldName: 'commissionRate',
      format: formatPercentage,
    },
    {
      label: 'Commission value',
      value: getValue(flatDeal, deal, 'commissionValue'),
      fieldName: 'commissionValue',
      format: (v) => (v === 'N/A' ? 'N/A' : formatCurrency(v)),
    },
    {
      label: 'Deal structure',
      value: getValue(flatDeal, deal, 'dealStructure'),
      fieldName: 'dealStructure',
    },
    {
      label: 'Equity/ownership (%)',
      value: getValue(flatDeal, deal, 'equity'),
      fieldName: 'equity',
      format: formatPercentage,
    },
    { label: 'Backend', value: getValue(flatDeal, deal, 'backend'), fieldName: 'backend' },
    {
      label: 'Expected close date',
      value: getValue(flatDeal, deal, 'closeDate'),
      fieldName: 'closeDate',
      format: formatDate,
    },
  ];

  const commonAttributionFields: DetailField[] = [
    {
      label: 'Leadership sponsor',
      value: getValue(flatDeal, deal, 'leadershipSponsor'),
      fieldName: 'leadershipSponsor',
    },
    { label: 'Source', value: getValue(flatDeal, deal, 'source'), fieldName: 'source' },
    {
      label: 'Partner account',
      value: getValue(flatDeal, deal, 'partnerAccount'),
      fieldName: 'partnerAccount',
    },
    {
      label: 'Partner contact',
      value: getValue(flatDeal, deal, 'partnerContact'),
      fieldName: 'partnerContact',
    },
    {
      label: 'Days in current stage',
      value: getValue(flatDeal, deal, 'daysInCurrentStage'),
      fieldName: 'daysInCurrentStage',
    },
  ];

  const commonAdditionalFields: DetailField[] = [
    { label: 'Outcome', value: getValue(flatDeal, deal, 'outcome'), fieldName: 'outcome' },
    {
      label: 'Loss reason',
      value: getValue(flatDeal, deal, 'lossReason'),
      fieldName: 'lossReason',
    },
    {
      label: 'Pass reason',
      value: getValue(flatDeal, deal, 'passReason'),
      fieldName: 'passReason',
    },
    { label: 'Competitor', value: getValue(flatDeal, deal, 'competitor'), fieldName: 'competitor' },
  ];

  let typeSpecificFields: DetailField[] = [];

  switch (dealType) {
    case 'Commercial/Endorsement':
      typeSpecificFields = [
        { label: 'Category', value: getValue(flatDeal, deal, 'category'), fieldName: 'category' },
        { label: 'Deal type', value: getValue(flatDeal, deal, 'dealType'), fieldName: 'dealType' },
        {
          label: 'Contract term (month)',
          value: getValue(flatDeal, deal, 'contractTerm'),
          fieldName: 'contractTerm',
          format: (v) => (v === 'N/A' ? 'N/A' : `${v} months`),
        },
        {
          label: 'Exclusivity scope',
          value: getValue(flatDeal, deal, 'exclusivityScope'),
          fieldName: 'exclusivityScope',
        },
        {
          label: 'Client fit score',
          value: getValue(flatDeal, deal, 'clientFitScore'),
          fieldName: 'clientFitScore',
        },
        {
          label: 'Client approval',
          value: getValue(flatDeal, deal, 'clientApproval'),
          fieldName: 'clientApproval',
        },
        {
          label: 'Renewal probability (%)',
          value: getValue(flatDeal, deal, 'renewalProbability'),
          fieldName: 'renewalProbability',
        },
      ];
      break;

    case 'Event Appearance':
      typeSpecificFields = [
        {
          label: 'Event type',
          value: getValue(flatDeal, deal, 'eventType'),
          fieldName: 'eventType',
        },
        {
          label: 'Event name',
          value: getValue(flatDeal, deal, 'eventName'),
          fieldName: 'eventName',
        },
        {
          label: 'Event date',
          value: getValue(flatDeal, deal, 'eventDate'),
          fieldName: 'eventDate',
          format: formatDate,
        },
        {
          label: 'Event location',
          value: getValue(flatDeal, deal, 'eventLocation'),
          fieldName: 'eventLocation',
        },
        {
          label: 'Duration (hours)',
          value: getValue(flatDeal, deal, 'duration'),
          fieldName: 'duration',
        },
        {
          label: 'Travel required',
          value: formatBoolean(flatDeal.travelRequired, deal.travelRequired),
          fieldName: 'travelRequired',
        },
        {
          label: 'Content rights',
          value: getValue(flatDeal, deal, 'contentRights'),
          fieldName: 'contentRights',
        },
        {
          label: 'Payment terms',
          value: getValue(flatDeal, deal, 'paymentTerms'),
          fieldName: 'paymentTerms',
        },
      ];
      break;

    case 'Event Owned':
      typeSpecificFields = [
        {
          label: 'Event property name',
          value: getValue(flatDeal, deal, 'eventPropertyName'),
          fieldName: 'eventPropertyName',
        },
        {
          label: 'Ownership structure',
          value: getValue(flatDeal, deal, 'ownershipStructure'),
          fieldName: 'ownershipStructure',
        },
        {
          label: '3AS Ownership (%)',
          value: getValue(flatDeal, deal, '3ASOwnership'),
          fieldName: '3ASOwnership',
          format: formatPercentage,
        },
        {
          label: 'Expected attendance',
          value: getValue(flatDeal, deal, 'expectedAttendance'),
          fieldName: 'expectedAttendance',
        },
        {
          label: 'Ticket revenue target ($)',
          value: getValue(flatDeal, deal, 'ticketRevenueTarget'),
          fieldName: 'ticketRevenueTarget',
          format: formatCurrency,
        },
        {
          label: 'Sponsorship revenue target ($)',
          value: getValue(flatDeal, deal, 'sponsorshipRevenueTarget'),
          fieldName: 'sponsorshipRevenueTarget',
          format: formatCurrency,
        },
        {
          label: 'Production budget ($)',
          value: getValue(flatDeal, deal, 'productionBudget'),
          fieldName: 'productionBudget',
          format: formatCurrency,
        },
        {
          label: 'Projected margin (%)',
          value: getValue(flatDeal, deal, 'projectedMargin'),
          fieldName: 'projectedMargin',
          format: formatPercentage,
        },
      ];
      break;

    case 'Content/Production':
      typeSpecificFields = [];
      break;

    case 'Licensing/IP':
      typeSpecificFields = [
        {
          label: 'License type',
          value: getValue(flatDeal, deal, 'licenseType'),
          fieldName: 'licenseType',
        },
        {
          label: 'AI rights flag',
          value: formatBoolean(flatDeal.AIRightsFlag, deal.AIRightsFlag),
          fieldName: 'AIRightsFlag',
        },
        {
          label: 'Guaranteed minimum ($)',
          value: getValue(flatDeal, deal, 'guaranteedMinimum'),
          fieldName: 'guaranteedMinimum',
          format: formatCurrency,
        },
        {
          label: 'Royalty rate (%)',
          value: getValue(flatDeal, deal, 'royaltyRate'),
          fieldName: 'royaltyRate',
          format: formatPercentage,
        },
        {
          label: 'Territory',
          value: getValue(flatDeal, deal, 'territory'),
          fieldName: 'territory',
        },
        {
          label: 'Product approval',
          value: getValue(flatDeal, deal, 'productApproval'),
          fieldName: 'productApproval',
        },
        {
          label: 'Reporting frequency',
          value: getValue(flatDeal, deal, 'reportingFrequency'),
          fieldName: 'reportingFrequency',
        },
      ];
      break;

    case 'Equity/Investment':
      typeSpecificFields = [
        {
          label: 'Investment type',
          value: getValue(flatDeal, deal, 'investmentType'),
          fieldName: 'investmentType',
        },
        {
          label: 'Entity name',
          value: getValue(flatDeal, deal, 'entityName'),
          fieldName: 'entityName',
        },
        {
          label: 'Industry/Sector',
          value: getValue(flatDeal, deal, 'industrySector'),
          fieldName: 'industrySector',
        },
        {
          label: 'Investment amount ($)',
          value: getValue(flatDeal, deal, 'investmentAmount'),
          fieldName: 'investmentAmount',
          format: formatCurrency,
        },
        {
          label: 'Ownership target (%)',
          value: getValue(flatDeal, deal, 'ownershipTarget'),
          fieldName: 'ownershipTarget',
          format: formatPercentage,
        },
        {
          label: 'Pre-Money valuation ($)',
          value: getValue(flatDeal, deal, 'preMoneyValuation'),
          fieldName: 'preMoneyValuation',
          format: formatCurrency,
        },
        {
          label: 'Board seat',
          value: getValue(flatDeal, deal, 'boardSeat'),
          fieldName: 'boardSeat',
        },
        {
          label: 'Target exit multiple',
          value: getValue(flatDeal, deal, 'targetExitMultiple'),
          fieldName: 'targetExitMultiple',
          format: (v) => (v === 'N/A' ? 'N/A' : `${v}x`),
        },
      ];
      break;

    case 'Branded Entertainment':
      typeSpecificFields = [
        {
          label: 'Buyer type',
          value: getValue(flatDeal, deal, 'buyerType'),
          fieldName: 'buyerType',
        },
        {
          label: 'Organization name',
          value: getValue(flatDeal, deal, 'organizationName'),
          fieldName: 'organizationName',
        },
        {
          label: 'Qualification score',
          value: getValue(flatDeal, deal, 'qualificationScore'),
          fieldName: 'qualificationScore',
        },
        {
          label: 'Client placement',
          value: getValue(flatDeal, deal, 'clientPlacement'),
          fieldName: 'clientPlacement',
        },
        {
          label: 'Placed clients',
          value: getValue(flatDeal, deal, 'placedClients'),
          fieldName: 'placedClients',
        },
        {
          label: 'Engagement type',
          value: getValue(flatDeal, deal, 'engagementType'),
          fieldName: 'engagementType',
        },
        {
          label: 'Development fee ($)',
          value: getValue(flatDeal, deal, 'developmentFee'),
          fieldName: 'developmentFee',
          format: formatCurrency,
        },
        {
          label: 'Retainer value ($)',
          value: getValue(flatDeal, deal, 'retainerValue'),
          fieldName: 'retainerValue',
          format: formatCurrency,
        },
        {
          label: 'IP ownership (%)',
          value: getValue(flatDeal, deal, 'IPOwnership'),
          fieldName: 'IPOwnership',
          format: formatPercentage,
        },
        {
          label: 'Lionsgate involved',
          value: formatBoolean(flatDeal.lionsgateInvolved, deal.lionsgateInvolved),
          fieldName: 'lionsgateInvolved',
        },
        {
          label: 'Recruiting pipeline',
          value: formatBoolean(flatDeal.recruitingPipeline, deal.recruitingPipeline),
          fieldName: 'recruitingPipeline',
        },
      ];
      break;
  }

  return {
    opportunityInfo: [
      ...commonOpportunityFieldsFirst,
      ...typeSpecificFields,
      ...commonOpportunityFieldsSecond,
    ],
    financialInfo: commonFinancialFields,
    attributionTracking: commonAttributionFields,
    additionalInfo: commonAdditionalFields,
    typeSpecificFields,
  };
};
